/**
 * Gurus Page - Investment Guru Tracking via Tikr.com
 * Track famous investors' buys and sells to inform trading decisions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { botAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import {
  RefreshCw, Search, ExternalLink, TrendingUp, TrendingDown,
  Award, Users, DollarSign, Calendar, Filter, Plus, Trash2, Edit2, Eye,
  Star, Briefcase, BarChart3, CheckCircle, AlertTriangle, Building2, Target
} from 'lucide-react';

// Famous investors/gurus data (expandable)
const FAMOUS_INVESTORS = [
  { 
    id: 'warren-buffett', 
    name: 'Warren Buffett', 
    fund: 'Berkshire Hathaway', 
    style: 'Value',
    aum: '$900B+',
    avatar: '🧓',
    description: 'The Oracle of Omaha - long-term value investor',
    tikrUrl: 'https://app.tikr.com/investor/warren-buffett',
  },
  { 
    id: 'michael-burry', 
    name: 'Michael Burry', 
    fund: 'Scion Asset Management', 
    style: 'Contrarian',
    aum: '$300M+',
    avatar: '🎯',
    description: 'Famous for "The Big Short" - contrarian investor',
    tikrUrl: 'https://app.tikr.com/investor/michael-burry',
  },
  { 
    id: 'ray-dalio', 
    name: 'Ray Dalio', 
    fund: 'Bridgewater Associates', 
    style: 'Macro',
    aum: '$150B+',
    avatar: '🌊',
    description: 'Largest hedge fund - macro strategy investor',
    tikrUrl: 'https://app.tikr.com/investor/ray-dalio',
  },
  { 
    id: 'cathie-wood', 
    name: 'Cathie Wood', 
    fund: 'ARK Invest', 
    style: 'Growth',
    aum: '$15B+',
    avatar: '🚀',
    description: 'Disruptive innovation - high growth focus',
    tikrUrl: 'https://app.tikr.com/investor/cathie-wood',
  },
  { 
    id: 'bill-ackman', 
    name: 'Bill Ackman', 
    fund: 'Pershing Square', 
    style: 'Activist',
    aum: '$18B+',
    avatar: '📢',
    description: 'Activist investor - concentrated positions',
    tikrUrl: 'https://app.tikr.com/investor/bill-ackman',
  },
  { 
    id: 'david-tepper', 
    name: 'David Tepper', 
    fund: 'Appaloosa Management', 
    style: 'Distressed',
    aum: '$6B+',
    avatar: '💎',
    description: 'Distressed debt specialist - opportunistic',
    tikrUrl: 'https://app.tikr.com/investor/david-tepper',
  },
  { 
    id: 'carl-icahn', 
    name: 'Carl Icahn', 
    fund: 'Icahn Enterprises', 
    style: 'Activist',
    aum: '$15B+',
    avatar: '🦅',
    description: 'Corporate raider - aggressive activist investor',
    tikrUrl: 'https://app.tikr.com/investor/carl-icahn',
  },
  { 
    id: 'chase-coleman', 
    name: 'Chase Coleman', 
    fund: 'Tiger Global', 
    style: 'Tech Growth',
    aum: '$50B+',
    avatar: '🐯',
    description: 'Tech-focused growth investor - Tiger cub',
    tikrUrl: 'https://app.tikr.com/investor/chase-coleman',
  },
];

// Investment style colors
const STYLE_COLORS = {
  'Value': 'blue',
  'Growth': 'green',
  'Contrarian': 'red',
  'Macro': 'purple',
  'Activist': 'orange',
  'Distressed': 'yellow',
  'Tech Growth': 'cyan',
};

// Guru card component
const GuruCard = ({ guru, isTracked, onToggleTrack, onViewDetails }) => {
  const styleColor = STYLE_COLORS[guru.style] || 'blue';
  
  return (
    <div className="card p-5 hover:border-accent-blue transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="text-4xl">{guru.avatar}</div>
          <div>
            <h3 className="font-semibold text-lg">{guru.name}</h3>
            <p className="text-text-muted text-sm">{guru.fund}</p>
          </div>
        </div>
        <button
          onClick={() => onToggleTrack(guru.id)}
          className={`p-2 rounded-lg transition-all ${
            isTracked 
              ? 'bg-accent-yellow/15 text-accent-yellow' 
              : 'bg-bg-tertiary text-text-muted hover:text-accent-yellow'
          }`}
          title={isTracked ? 'Untrack' : 'Track this investor'}
        >
          <Star className={`w-5 h-5 ${isTracked ? 'fill-current' : ''}`} />
        </button>
      </div>
      
      <p className="text-text-secondary text-sm mb-4">{guru.description}</p>
      
      <div className="flex items-center gap-2 mb-4">
        <span className={`badge badge-${styleColor}`}>{guru.style}</span>
        <span className="text-text-muted text-sm">AUM: {guru.aum}</span>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={() => onViewDetails(guru)}
          className="flex-1 btn-secondary py-2 text-sm flex items-center justify-center gap-2"
        >
          <Eye className="w-4 h-4" /> View Holdings
        </button>
        <a
          href={guru.tikrUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary py-2 px-3 text-sm flex items-center gap-1"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

// Guru detail modal with holdings
const GuruDetailModal = ({ guru, onClose, trackedSymbols, onAddToWatchlist }) => {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filingDate, setFilingDate] = useState(null);
  const [totalValue, setTotalValue] = useState(0);
  
  useEffect(() => {
    const fetchRealHoldings = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await botAPI.getGuruHoldings(guru.id);
        const data = res.data;
        
        if (data.error && (!data.holdings || data.holdings.length === 0)) {
          setError(data.error);
          setHoldings([]);
        } else {
          setFilingDate(data.filingDate);
          setTotalValue(data.totalValue || 0);
          setHoldings((data.holdings || []).map(h => ({
            symbol: h.symbol,
            name: h.name,
            shares: h.shares,
            value: h.value,
            weight: h.weight,
            change: 0, // Would need live price data
          })));
        }
      } catch (err) {
        console.error('Failed to fetch guru holdings:', err);
        setError('Failed to fetch holdings from SEC EDGAR. Try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchRealHoldings();
  }, [guru]);
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bg-secondary rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-border-color">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-5xl">{guru.avatar}</div>
              <div>
                <h2 className="font-display text-2xl font-bold">{guru.name}</h2>
                <p className="text-text-secondary">{guru.fund} • {guru.style}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={guru.tikrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary py-2 px-4 flex items-center gap-2"
              >
                View on Tikr <ExternalLink className="w-4 h-4" />
              </a>
              <button onClick={onClose} className="btn-secondary p-2">✕</button>
            </div>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-accent-blue" />
              <span className="ml-3 text-text-muted">Fetching 13F data from SEC EDGAR...</span>
            </div>
          ) : error && holdings.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-accent-yellow mx-auto mb-4" />
              <p className="text-text-secondary">{error}</p>
              <p className="text-text-muted text-sm mt-2">SEC EDGAR may be temporarily unavailable. Try again later.</p>
            </div>
          ) : (
            <>
              {/* Filing Info Banner */}
              {filingDate && (
                <div className="mb-4 p-3 bg-accent-blue/10 border border-accent-blue/20 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-accent-blue" />
                    <span className="text-text-secondary">13F Filing Date: <strong className="text-text-primary">{filingDate}</strong></span>
                  </div>
                  <div className="text-sm text-text-muted">
                    Total Value: <strong className="text-accent-green">${(totalValue / 1000000).toFixed(0)}M</strong> • {holdings.length} positions
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Holdings */}
              <div>
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-accent-purple" />
                  Top Holdings (SEC 13F)
                </h3>
                <div className="space-y-2">
                  {holdings.slice(0, 15).map((holding, idx) => (
                    <div 
                      key={holding.symbol}
                      className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-text-muted w-6 text-sm">{idx + 1}</span>
                        <div>
                          <div className="font-mono font-semibold">{holding.symbol}</div>
                          <div className="text-text-muted text-xs truncate max-w-[120px]">{holding.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">{holding.weight}%</div>
                        <div className="text-xs text-text-muted">${(holding.value / 1000000).toFixed(1)}M</div>
                      </div>
                      <button
                        onClick={() => onAddToWatchlist(holding.symbol)}
                        className={`p-1.5 rounded-lg transition-all ${
                          trackedSymbols.includes(holding.symbol)
                            ? 'bg-accent-green/15 text-accent-green'
                            : 'bg-bg-card text-text-muted hover:text-accent-green'
                        }`}
                        title="Add to watchlist"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Holdings continued + stats */}
              <div>
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-accent-blue" />
                  Portfolio Breakdown
                </h3>
                {holdings.length > 0 && (
                  <div className="space-y-3">
                    {/* Top 5 weight bars */}
                    {holdings.slice(0, 8).map((h) => (
                      <div key={h.symbol + '-bar'} className="flex items-center gap-3">
                        <span className="font-mono text-sm w-12">{h.symbol}</span>
                        <div className="flex-1 bg-bg-tertiary rounded-full h-4 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-accent-blue to-accent-purple"
                            style={{ width: `${Math.min(h.weight, 100)}%` }}
                          />
                        </div>
                        <span className="font-mono text-sm text-text-muted w-14 text-right">{h.weight}%</span>
                      </div>
                    ))}
                    
                    {/* Quick stats */}
                    <div className="mt-6 grid grid-cols-2 gap-3">
                      <div className="bg-bg-tertiary rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-accent-blue">{holdings.length}</div>
                        <div className="text-text-muted text-xs">Resolved Positions</div>
                      </div>
                      <div className="bg-bg-tertiary rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-accent-green">${(totalValue / 1000000000).toFixed(1)}B</div>
                        <div className="text-text-muted text-xs">Total 13F Value</div>
                      </div>
                      <div className="bg-bg-tertiary rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold">{holdings[0]?.symbol || '-'}</div>
                        <div className="text-text-muted text-xs">Largest Holding</div>
                      </div>
                      <div className="bg-bg-tertiary rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold">{holdings[0]?.weight || 0}%</div>
                        <div className="text-text-muted text-xs">Top Weight</div>
                      </div>
                    </div>
                    
                    {/* Add all to watchlist button */}
                    <button
                      onClick={() => {
                        holdings.slice(0, 20).forEach(h => onAddToWatchlist(h.symbol));
                        toast.success(`Added top ${Math.min(20, holdings.length)} holdings to watchlist`);
                      }}
                      className="w-full mt-4 btn-primary py-2 flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" /> Add Top Holdings to Watchlist
                    </button>
                  </div>
                )}
              </div>
              </div>
              
              {/* 13F Disclaimer */}
              <div className="col-span-1 lg:col-span-2 mt-2 p-3 bg-accent-yellow/10 border border-accent-yellow/20 rounded-lg">
                <p className="text-xs text-accent-yellow flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Data sourced from SEC EDGAR 13F-HR filings. These are delayed 45 days from quarter-end. Holdings may have changed since the last filing.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Tikr.com connection card
const TikrConnectionCard = ({ isConnected, tikrCredentials, onConnect, onDisconnect }) => {
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [apiKey, setApiKey] = useState('');
  
  const handleConnect = () => {
    if (email && apiKey) {
      onConnect({ email, apiKey });
      setShowForm(false);
      setEmail('');
      setApiKey('');
    }
  };
  
  return (
    <div className="card p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold">Tikr.com Integration</h2>
            <p className="text-text-muted text-sm">Track institutional investors and 13F filings</p>
          </div>
        </div>
        {isConnected ? (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-accent-green/15 rounded-lg">
              <CheckCircle className="w-4 h-4 text-accent-green" />
              <span className="text-accent-green text-sm">Connected</span>
            </div>
            <button onClick={onDisconnect} className="btn-secondary p-2 text-accent-red">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Connect Tikr
          </button>
        )}
      </div>
      
      {showForm && !isConnected && (
        <div className="border-t border-border-color pt-4 mt-4">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Tikr Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input"
              />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">API Key (optional)</label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Your Tikr API key"
                className="input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <a 
              href="https://tikr.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-accent-blue text-sm hover:underline flex items-center gap-1"
            >
              Don't have an account? Sign up <ExternalLink className="w-3 h-3" />
            </a>
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleConnect} className="btn-primary">Connect</button>
            </div>
          </div>
        </div>
      )}
      
      {!isConnected && !showForm && (
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="text-center p-3 bg-bg-tertiary rounded-lg">
            <Users className="w-6 h-6 text-accent-blue mx-auto mb-2" />
            <div className="text-sm text-text-secondary">Track 1000+ Funds</div>
          </div>
          <div className="text-center p-3 bg-bg-tertiary rounded-lg">
            <Calendar className="w-6 h-6 text-accent-purple mx-auto mb-2" />
            <div className="text-sm text-text-secondary">13F Filings</div>
          </div>
          <div className="text-center p-3 bg-bg-tertiary rounded-lg">
            <TrendingUp className="w-6 h-6 text-accent-green mx-auto mb-2" />
            <div className="text-sm text-text-secondary">Buy/Sell Alerts</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tracked symbols watchlist
const TrackedSymbolsList = ({ symbols, onRemove }) => {
  if (symbols.length === 0) return null;
  
  return (
    <div className="card p-4 mb-6">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <Star className="w-4 h-4 text-accent-yellow" />
        Guru-Tracked Symbols ({symbols.length})
      </h3>
      <div className="flex flex-wrap gap-2">
        {symbols.map(symbol => (
          <span 
            key={symbol}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-accent-yellow/10 text-accent-yellow rounded-lg text-sm"
          >
            {symbol}
            <button onClick={() => onRemove(symbol)} className="hover:text-accent-red ml-1">
              <Trash2 className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

// Main Gurus Page
const Gurus = () => {
  const { hasAlpacaKeys } = useAuth();
  const [trackedGurus, setTrackedGurus] = useState(['warren-buffett', 'michael-burry']);
  const [trackedSymbols, setTrackedSymbols] = useState(['AAPL', 'OXY', 'GOOGL']);
  const [tikrConnected, setTikrConnected] = useState(false);
  const [tikrCredentials, setTikrCredentials] = useState(null);
  const [selectedGuru, setSelectedGuru] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStyle, setFilterStyle] = useState('all');
  const [showOnlyTracked, setShowOnlyTracked] = useState(false);
  
  // Load saved state from localStorage
  useEffect(() => {
    const savedGurus = localStorage.getItem('paeru_tracked_gurus');
    const savedSymbols = localStorage.getItem('paeru_tracked_symbols');
    const savedTikr = localStorage.getItem('paeru_tikr_connected');
    
    if (savedGurus) setTrackedGurus(JSON.parse(savedGurus));
    if (savedSymbols) setTrackedSymbols(JSON.parse(savedSymbols));
    if (savedTikr) setTikrConnected(true);
  }, []);
  
  // Save state to localStorage
  useEffect(() => {
    localStorage.setItem('paeru_tracked_gurus', JSON.stringify(trackedGurus));
    localStorage.setItem('paeru_tracked_symbols', JSON.stringify(trackedSymbols));
  }, [trackedGurus, trackedSymbols]);
  
  const toggleTrackGuru = (guruId) => {
    setTrackedGurus(prev => 
      prev.includes(guruId) 
        ? prev.filter(id => id !== guruId)
        : [...prev, guruId]
    );
  };
  
  const addToWatchlist = (symbol) => {
    if (!trackedSymbols.includes(symbol)) {
      setTrackedSymbols(prev => [...prev, symbol]);
      toast.success(`Added ${symbol} to watchlist`);
    }
  };
  
  const removeFromWatchlist = (symbol) => {
    setTrackedSymbols(prev => prev.filter(s => s !== symbol));
  };
  
  const handleTikrConnect = (credentials) => {
    setTikrCredentials(credentials);
    setTikrConnected(true);
    localStorage.setItem('paeru_tikr_connected', 'true');
    toast.success('Connected to Tikr.com!');
  };
  
  const handleTikrDisconnect = () => {
    setTikrCredentials(null);
    setTikrConnected(false);
    localStorage.removeItem('paeru_tikr_connected');
    toast.success('Disconnected from Tikr');
  };
  
  // Filter gurus
  const filteredGurus = FAMOUS_INVESTORS.filter(guru => {
    if (showOnlyTracked && !trackedGurus.includes(guru.id)) return false;
    if (filterStyle !== 'all' && guru.style !== filterStyle) return false;
    if (searchQuery && !guru.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !guru.fund.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  
  const styles = [...new Set(FAMOUS_INVESTORS.map(g => g.style))];
  
  return (
    <div className="min-h-screen">
      <Navigation 
        title="Investment Gurus" 
        subtitle="Track legendary investors via Tikr.com"
      />
      
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
      {/* Tikr Connection */}
      <TikrConnectionCard
        isConnected={tikrConnected}
        tikrCredentials={tikrCredentials}
        onConnect={handleTikrConnect}
        onDisconnect={handleTikrDisconnect}
      />
      
      {/* Tracked Symbols */}
      <TrackedSymbolsList symbols={trackedSymbols} onRemove={removeFromWatchlist} />
      
      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="flex items-center gap-2 px-4 py-2 bg-bg-tertiary rounded-xl flex-1 min-w-[200px] max-w-xs">
            <Search className="w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search investors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-text-primary placeholder:text-text-muted w-full"
            />
          </div>
          
          {/* Style filter */}
          <select
            value={filterStyle}
            onChange={(e) => setFilterStyle(e.target.value)}
            className="bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Styles</option>
            {styles.map(style => (
              <option key={style} value={style}>{style}</option>
            ))}
          </select>
          
          {/* Show tracked only */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyTracked}
              onChange={(e) => setShowOnlyTracked(e.target.checked)}
              className="w-4 h-4 rounded border-border-color"
            />
            <span className="text-sm text-text-secondary">Tracked only</span>
          </label>
          
          {/* Stats */}
          <div className="ml-auto flex items-center gap-4 text-sm text-text-muted">
            <span>Tracking {trackedGurus.length} gurus</span>
            <span>•</span>
            <span>{trackedSymbols.length} symbols</span>
          </div>
        </div>
      </div>
      
      {/* Gurus Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredGurus.map(guru => (
          <GuruCard
            key={guru.id}
            guru={guru}
            isTracked={trackedGurus.includes(guru.id)}
            onToggleTrack={toggleTrackGuru}
            onViewDetails={setSelectedGuru}
          />
        ))}
        {filteredGurus.length === 0 && (
          <div className="col-span-full card p-12 text-center">
            <Award className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Investors Found</h3>
            <p className="text-text-secondary">Try adjusting your filters or search query.</p>
          </div>
        )}
      </div>
      
      {/* Info card */}
      <div className="mt-8 card p-6 bg-gradient-to-r from-accent-blue/10 to-accent-purple/10 border-accent-blue/20">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-accent-blue" />
          How Guru Tracking Works
        </h3>
        <p className="text-text-secondary text-sm mb-4">
          Institutional investors managing over $100M must file 13F reports quarterly with the SEC, 
          disclosing their equity holdings. While these filings are delayed by 45 days, they provide 
          valuable insights into what successful investors are buying and selling.
        </p>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-accent-green flex-shrink-0 mt-0.5" />
            <span className="text-text-secondary">Track top holdings and position changes</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-accent-green flex-shrink-0 mt-0.5" />
            <span className="text-text-secondary">Add guru picks to your watchlist</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-accent-green flex-shrink-0 mt-0.5" />
            <span className="text-text-secondary">Combine with AI analysis for better signals</span>
          </div>
        </div>
      </div>
      
      {/* Guru Detail Modal */}
      {selectedGuru && (
        <GuruDetailModal
          guru={selectedGuru}
          onClose={() => setSelectedGuru(null)}
          trackedSymbols={trackedSymbols}
          onAddToWatchlist={addToWatchlist}
        />
      )}
      </div>
    </div>
  );
};

export default Gurus;
